import React from "react"
import {
  Box,
  Button,
  Flex,
  HStack,
} from "@chakra-ui/react"
import { PassBuilderBodyText } from "./PassBuilderTypography"
import { DROP_ZONE_BG, DROP_ZONE_BORDER } from "./passBuilderTokens"

type Props = {
  dropLabel: string
  width: number
  height: number
  imageUrl?: string
  imagePreview?: React.ReactNode
  onUpload?: () => void
  onRemove?: () => void
  isUploading?: boolean
  imageFit?: "contain" | "cover"
  /** False for slots that always render something, so there is nothing to clear. */
  isRemovable?: boolean
}

const hasImage = (
  imageUrl?: string,
  imagePreview?: React.ReactNode,
) => Boolean(imagePreview || imageUrl)

const PassBuilderUploadDropzone = ({
  dropLabel,
  width,
  height,
  imageUrl,
  imagePreview,
  onUpload,
  onRemove,
  isUploading,
  imageFit = "contain",
  isRemovable = true,
}: Props) => (
  <HStack align="center" spacing={3}>
    <Flex
      w={`${width}px`}
      h={`${height}px`}
      flexShrink={0}
      borderWidth="2px"
      borderStyle="dashed"
      borderColor={DROP_ZONE_BORDER}
      borderRadius="5px"
      bg={DROP_ZONE_BG}
      align="center"
      justify="center"
      overflow="hidden"
      cursor="pointer"
      _hover={{ borderColor: "blue.400", bg: "blue.50" }}
      onClick={onUpload}
      // The dashed box already clips at its inner curve; a radius on the preview
      // itself rounds more tightly than that and shows as mismatched corners.
      sx={{ "& img": { borderRadius: 0 } }}
    >
      {imagePreview || (imageUrl ? (
        <Box
          as="img"
          src={imageUrl}
          w="full"
          h="full"
          objectFit={imageFit}
        />
      ) : (
        <PassBuilderBodyText textAlign="center" px={2}>
          {dropLabel}
        </PassBuilderBodyText>
      ))}
    </Flex>
    <Button
      size="sm"
      variant="outline"
      onClick={onUpload}
      isLoading={isUploading}
      loadingText="Uploading"
      minW="78px"
      h="32px"
      flexShrink={0}
      bg="white"
      borderColor="blue.500"
      color="blue.500"
      fontWeight="medium"
    >
      Upload
    </Button>
    {isRemovable && hasImage(imageUrl, imagePreview) && onRemove && (
      <Button
        size="sm"
        variant="outline"
        onClick={onRemove}
        minW="78px"
        h="32px"
        flexShrink={0}
        bg="white"
        borderColor="red.500"
        color="red.500"
        fontWeight="medium"
      >
        Remove
      </Button>
    )}
  </HStack>
)

export default PassBuilderUploadDropzone
